import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  BookOpenText,
  Brain,
  BriefcaseBusiness,
  Building2,
  CarFront,
  ChefHat,
  Clock3,
  Compass,
  Cpu,
  FlaskConical,
  Globe2,
  GraduationCap,
  Handshake,
  HeartPulse,
  Landmark,
  Leaf,
  Lock,
  MapPinned,
  MessageCircleMore,
  Mountain,
  Package2,
  Palette,
  PawPrint,
  PersonStanding,
  Ruler,
  Scale,
  Shirt,
  Sofa,
  Sparkles,
  UsersRound,
  Wrench,
} from 'lucide-react';
import MainMenu from '../../components/MainMenu/MainMenu';
import { fetchData } from '../../api/api';
import { fetchPathSteps } from '../../api/fetchPath';
import { fetchLessonTopics } from '../../api/fetchLessons';
import { useAppConfig } from '../../context/AppConfigContext';
import { useAssistant } from '../../components/Assistant/AssistantContext';
import styles from './PathHome.module.css';

import dailyImg from '../../assets/images/500x500/daily.png';
import workImg from '../../assets/images/500x500/work.png';
import foodAndDrinksImg from '../../assets/images/500x500/food_and_drinks.png';
import clothingImg from '../../assets/images/500x500/clothing.png';
import transportImg from '../../assets/images/500x500/transport.png';
import weatherImg from '../../assets/images/500x500/weather.png';
import doctorImg from '../../assets/images/500x500/doctor.png';
import inTheCityImg from '../../assets/images/500x500/in_the_city.png';
import vacationImg from '../../assets/images/500x500/vacation.png';
import supermarketImg from '../../assets/images/500x500/supermarket.png';
import bankAndMoneyImg from '../../assets/images/500x500/bank_and_money.png';
import humanBodyImg from '../../assets/images/500x500/human_body.png';
import familyImg from '../../assets/images/500x500/family.png';
import natureImg from '../../assets/images/500x500/nature.png';
import animalsAndBirdsImg from '../../assets/images/500x500/animals_and_birds.png';

const LEVEL_ORDER = ['beginner', 'advanced', 'expert'];
const PRIMARY_STEP_ORDER = ['learn', 'easy', 'medium'];
const MILESTONE_STEP_ORDER = ['dialog', 'exam', 'review_easy', 'review_medium'];
const LEVEL_MILESTONE_STEP_ORDER = ['level_review_easy', 'level_review_medium'];

const TOPIC_IMAGES = {
  daily: dailyImg,
  work: workImg,
  food_and_drinks: foodAndDrinksImg,
  clothing: clothingImg,
  transport: transportImg,
  weather: weatherImg,
  doctor: doctorImg,
  in_the_city: inTheCityImg,
  vacation: vacationImg,
  supermarket: supermarketImg,
  bank_and_money: bankAndMoneyImg,
  human_body: humanBodyImg,
  family: familyImg,
  nature: natureImg,
  animals_and_birds: animalsAndBirdsImg,
};

const TOPIC_IMAGE_ALIASES = {
  animal: animalsAndBirdsImg,
  'art/culture': dailyImg,
  'body/health': humanBodyImg,
  'building/place': inTheCityImg,
  clothing: clothingImg,
  communication: dailyImg,
  'communication/media': dailyImg,
  'economy/trade': bankAndMoneyImg,
  'education/science': doctorImg,
  'emotion/feeling': familyImg,
  event: dailyImg,
  'food/drink': foodAndDrinksImg,
  'geography/location': inTheCityImg,
  'household/object': dailyImg,
  'material/substance': natureImg,
  movement: dailyImg,
  'nature/phenomena': natureImg,
  'object/thing': dailyImg,
  person: familyImg,
  'physical action': dailyImg,
  'place/direction': inTheCityImg,
  plant: natureImg,
  'politics/law': bankAndMoneyImg,
  quality: dailyImg,
  'technology/device': workImg,
  time: dailyImg,
  'time/date': dailyImg,
  'tool/instrument': workImg,
  'vehicle/transport': transportImg,
  'work/activity': workImg,
  'work/profession': workImg,
};

const getPathCopy = (t) => {
  const path = t?.path || {};

  return {
    switchPath: path.switchPath || 'Path',
    switchModule: path.switchModule || 'Modul',
    loading: path.loading || 'Loading...',
    empty: path.empty || 'No path steps found',
    activeLevel: path.activeLevel || 'In progress',
    hasProgress: path.hasProgress || 'Has progress',
    notStarted: path.notStarted || 'Not started yet',
    activeRecall: path.activeRecall || 'Active Recall',
    speakingPractice: path.speakingPractice || 'building speech',
    fallbackTopic: path.fallbackTopic || 'Topic',
    lessonsTitle: path.lessons_title || 'Zero Level (A0 - A1)',
    lessonsSubtitle: path.lessons_subtitle || 'Topics and subtopics before Beginner.',
    lessonsBadge: path.lessons_badge || 'Lessons',
    lessonsOpen: path.lessons_open || 'Open',
    lessonsThemes: path.lessons_themes || 'Themes',
    lessonsSubthemes: path.lessons_subthemes || 'Subtopics',
    lessonsWords: path.lessons_words || 'Words',
    lockedLabel: path.path_locked_label || 'VIP',
    lockedHint: path.path_locked_hint || 'Open with Premium',
    levelLabels: {
      beginner: path.level_beginner || 'Beginner (A1 - A2)',
      advanced: path.level_advanced || 'Advanced (A2 - B1)',
      expert: path.level_expert || 'Expert (B1-B2+)',
    },
    stepTypeLabels: {
      learn: path.step_learn || 'Words',
      easy: path.step_easy || 'Hints',
      medium: path.step_medium || 'Profi',
    },
    milestoneLabels: {
      dialog: path.milestone_dialog || 'Dialog',
      exam: path.milestone_exam || 'Exam',
      review_easy: path.milestone_review_easy || 'With hints',
      review_medium: path.milestone_review_medium || 'Without hints',
    },
    milestoneShortLabels: {
      dialog: path.milestone_short_dialog || 'D',
      exam: path.milestone_short_exam || 'E',
      review_easy: path.milestone_short_review_easy || 'Easy',
      review_medium: path.milestone_short_review_medium || 'Hard',
    },
    levelMilestoneEyebrow: path.level_review_eyebrow || 'Level finale',
    levelMilestoneTitle: path.level_review_title || 'Consolidate the whole level',
    levelMilestoneText:
      path.level_review_text || 'Two final passes through all words of this level before you move on.',
    levelMilestoneMeta: path.level_review_meta || 'All words of the level',
    levelMilestoneLabels: {
      level_review_easy: path.level_review_easy || 'With support',
      level_review_medium: path.level_review_medium || 'Solo check',
    },
    levelMilestoneDescriptions: {
      level_review_easy: path.level_review_easy_subtitle || 'All level words with hints',
      level_review_medium: path.level_review_medium_subtitle || 'All level words without hints',
    },
  };
};

const TOPIC_ICON_MAP = {
  'abstract/discourse_content': BookOpenText,
  'abstract/meta_structure': BookOpenText,
  'abstract/norms_risk': Scale,
  'abstract/planning_outcome': BookOpenText,
  'abstract/process_change': Compass,
  'abstract/society_relations': Handshake,
  'abstract/state_quality_eval': Sparkles,
  'administrative actions': Landmark,
  animal: PawPrint,
  'art/culture': Palette,
  'body/health': HeartPulse,
  'building/place': Building2,
  causation: Compass,
  'cause/reason': BookOpenText,
  'change of state': Compass,
  clothing: Shirt,
  cognition: Brain,
  color: Palette,
  communication: MessageCircleMore,
  'communication/media': MessageCircleMore,
  concession: BookOpenText,
  consumption: ChefHat,
  'creativity/arts': Palette,
  'degree/intensity': Activity,
  'economy/trade': Scale,
  'education/science': GraduationCap,
  'emotion/feeling': HeartPulse,
  event: Globe2,
  'food/drink': ChefHat,
  frequency: Clock3,
  'geography/location': MapPinned,
  'household actions': Sofa,
  'household/object': Sofa,
  manner: Sparkles,
  'material/substance': FlaskConical,
  'measurement/evaluation': Ruler,
  movement: PersonStanding,
  'nature/phenomena': Mountain,
  'object/thing': Package2,
  other: Sparkles,
  perception: Brain,
  person: UsersRound,
  'physical action': PersonStanding,
  'place/direction': MapPinned,
  plant: Leaf,
  'politics/law': Landmark,
  possession: Package2,
  quality: Sparkles,
  'quantity/measurement': Ruler,
  'religion/belief': Landmark,
  'shape/size': Ruler,
  'social interaction': Handshake,
  'sport/game': Activity,
  state: Activity,
  'teaching/learning': GraduationCap,
  'technology/device': Cpu,
  temporal: Clock3,
  time: Clock3,
  'time/date': Clock3,
  'tool/instrument': Wrench,
  'vehicle/transport': CarFront,
  volition: Compass,
  'work/activity': BriefcaseBusiness,
  'work/profession': BriefcaseBusiness,
};

const TOPIC_ACCENT_MAP = {
  animal: { color: '#a855f7', glow: 'rgba(168, 85, 247, 0.24)' },
  'art/culture': { color: '#ec4899', glow: 'rgba(236, 72, 153, 0.22)' },
  'body/health': { color: '#ef4444', glow: 'rgba(239, 68, 68, 0.22)' },
  'building/place': { color: '#0f766e', glow: 'rgba(15, 118, 110, 0.22)' },
  clothing: { color: '#f97316', glow: 'rgba(249, 115, 22, 0.24)' },
  communication: { color: '#0284c7', glow: 'rgba(2, 132, 199, 0.22)' },
  'communication/media': { color: '#0284c7', glow: 'rgba(2, 132, 199, 0.22)' },
  'economy/trade': { color: '#ca8a04', glow: 'rgba(202, 138, 4, 0.22)' },
  'education/science': { color: '#4f46e5', glow: 'rgba(79, 70, 229, 0.22)' },
  'emotion/feeling': { color: '#db2777', glow: 'rgba(219, 39, 119, 0.22)' },
  event: { color: '#6366f1', glow: 'rgba(99, 102, 241, 0.22)' },
  'food/drink': { color: '#ea580c', glow: 'rgba(234, 88, 12, 0.22)' },
  'geography/location': { color: '#0891b2', glow: 'rgba(8, 145, 178, 0.22)' },
  'household/object': { color: '#0f766e', glow: 'rgba(15, 118, 110, 0.18)' },
  'material/substance': { color: '#64748b', glow: 'rgba(100, 116, 139, 0.18)' },
  movement: { color: '#2563eb', glow: 'rgba(37, 99, 235, 0.18)' },
  'nature/phenomena': { color: '#16a34a', glow: 'rgba(22, 163, 74, 0.2)' },
  'object/thing': { color: '#0f766e', glow: 'rgba(15, 118, 110, 0.18)' },
  person: { color: '#2563eb', glow: 'rgba(37, 99, 235, 0.18)' },
  'physical action': { color: '#dc2626', glow: 'rgba(220, 38, 38, 0.18)' },
  plant: { color: '#16a34a', glow: 'rgba(22, 163, 74, 0.2)' },
  'politics/law': { color: '#7c3aed', glow: 'rgba(124, 58, 237, 0.2)' },
  quality: { color: '#d97706', glow: 'rgba(217, 119, 6, 0.2)' },
  'technology/device': { color: '#2563eb', glow: 'rgba(37, 99, 235, 0.18)' },
  'time/date': { color: '#0891b2', glow: 'rgba(8, 145, 178, 0.2)' },
  'tool/instrument': { color: '#7c2d12', glow: 'rgba(124, 45, 18, 0.2)' },
  'vehicle/transport': { color: '#2563eb', glow: 'rgba(37, 99, 235, 0.2)' },
  'work/activity': { color: '#1d4ed8', glow: 'rgba(29, 78, 216, 0.2)' },
  'work/profession': { color: '#1d4ed8', glow: 'rgba(29, 78, 216, 0.2)' },
};

const formatTopicLabel = (value, fallbackLabel) => {
  if (!value) return fallbackLabel;
  return String(value)
    .replace(/\s+-\s+/g, ' - ')
    .replace(/_/g, ' ')
    .trim();
};

const getTopicTitle = (step, fallbackLabel) =>
  formatTopicLabel(step.title_ru || step.title_ua || step.title_en || step.title_de || step.chunk_code, fallbackLabel);

const getStepTopicCode = (step) => {
  if (step?.step_type === 'dialog' || step?.step_type === 'exam') {
    return step?.repeat_pool?.topicCode || step?.topics?.[0]?.code || null;
  }
  return step?.topics?.[0]?.code || step?.repeat_pool?.topicCode || null;
};

const isReviewStepType = (stepType) => String(stepType || '').includes('review');

const getStepTopicImage = (step) => {
  const topicCode = getStepTopicCode(step);
  if (!topicCode) return null;
  if (isReviewStepType(step?.step_type)) {
    return null;
  }
  return TOPIC_IMAGES[topicCode] || TOPIC_IMAGE_ALIASES[topicCode] || null;
};

const getStepTopicIcon = (step) => TOPIC_ICON_MAP[getStepTopicCode(step)] || Compass;

const getStepTopicAccent = (step) =>
  TOPIC_ACCENT_MAP[getStepTopicCode(step)] || { color: '#2563eb', glow: 'rgba(37, 99, 235, 0.18)' };

const getStepCompletion = (step) => {
  const total = Number(step?.total_items) || 0;
  const progress = Math.min(Number(step?.progress_count) || 0, total);
  const percent = total > 0 ? Math.round((progress / total) * 100) : 0;
  const completed = Boolean(step?.completed) || (total > 0 && progress >= total);
  return { total, progress, percent, completed };
};

const getLastActiveStep = (steps) => {
  const withProgress = steps.filter(
    (step) => Number(step?.progress_count) > 0 || Boolean(step?.completed) || Boolean(step?.updated_at),
  );
  if (withProgress.length === 0) return null;

  return [...withProgress].sort((a, b) => {
    const timeA = a?.updated_at ? new Date(a.updated_at).getTime() : 0;
    const timeB = b?.updated_at ? new Date(b.updated_at).getTime() : 0;
    if (timeA !== timeB) return timeB - timeA;
    return (Number(b.sort_order) || 0) - (Number(a.sort_order) || 0);
  })[0];
};

const getFocusType = (chunk, lastActiveStepId) => {
  const stepsByType = chunk.stepsByType || {};
  const activeType = PRIMARY_STEP_ORDER.find((stepType) => stepsByType[stepType]?.id === lastActiveStepId);
  if (activeType) return activeType;

  const inProgressType = PRIMARY_STEP_ORDER.find((stepType) => {
    const step = stepsByType[stepType];
    if (!step) return false;
    const { progress, completed } = getStepCompletion(step);
    return progress > 0 && !completed;
  });
  if (inProgressType) return inProgressType;

  const firstIncompleteType = PRIMARY_STEP_ORDER.find((stepType) => {
    const step = stepsByType[stepType];
    if (!step) return false;
    return !getStepCompletion(step).completed;
  });
  if (firstIncompleteType) return firstIncompleteType;

  return PRIMARY_STEP_ORDER.find((stepType) => stepsByType[stepType]) || 'learn';
};

const getMilestoneSteps = (chunk) =>
  MILESTONE_STEP_ORDER.flatMap((stepType) => chunk.milestoneByType?.[stepType] || []);

const hasMilestoneBlock = (chunk) =>
  MILESTONE_STEP_ORDER.some((stepType) => (chunk.milestoneByType?.[stepType] || []).length > 0);

const getLevelMilestoneSteps = (level) =>
  LEVEL_MILESTONE_STEP_ORDER.flatMap((stepType) => level.levelMilestonesByType?.[stepType] || []);

const hasLevelMilestoneBlock = (level) =>
  LEVEL_MILESTONE_STEP_ORDER.some((stepType) => (level.levelMilestonesByType?.[stepType] || []).length > 0);

const getLessonOverview = (lessons) => ({
  themes: lessons.length,
  subthemes: lessons.reduce(
    (sum, lesson) => sum + (Array.isArray(lesson?.blocks) ? lesson.blocks.length : 0),
    0,
  ),
  words: lessons.reduce((sum, lesson) => sum + (Number(lesson?.word_count) || 0), 0),
});

const isChunkFullyCompleted = (chunk) =>
  PRIMARY_STEP_ORDER.every((stepType) => {
    const step = chunk?.stepsByType?.[stepType];
    return step ? getStepCompletion(step).completed : false;
  });

const MILESTONE_LAYOUT = [
  { left: '50%', top: '9%' },
  { left: '78%', top: '22%' },
  { left: '91%', top: '50%' },
  { left: '78%', top: '78%' },
  { left: '50%', top: '91%' },
  { left: '22%', top: '78%' },
  { left: '9%', top: '50%' },
  { left: '22%', top: '22%' },
];

const MILESTONE_ARROWS = [
  { left: '65%', top: '15%', rotate: '28deg' },
  { left: '84%', top: '35%', rotate: '72deg' },
  { left: '84%', top: '65%', rotate: '116deg' },
  { left: '65%', top: '84%', rotate: '160deg' },
  { left: '35%', top: '84%', rotate: '204deg' },
  { left: '16%', top: '65%', rotate: '248deg' },
  { left: '16%', top: '35%', rotate: '292deg' },
  { left: '35%', top: '15%', rotate: '336deg' },
];

const DEFAULT_PUT_HINT_COUNTS = Object.freeze({
  vopros: 0,
  dialog: 0,
  nullteil: 0,
});

const buildLevelGroups = (steps, lastActiveStep, copy) => {
  const levels = {};

  steps.forEach((step) => {
    const levelKey = step.level_group || 'beginner';
    const topicTitle = getTopicTitle(step, copy.fallbackTopic);
    const chunkKey = String(step.chunk_id || `chunk_${step.id}`);

    if (!levels[levelKey]) {
      levels[levelKey] = {
        key: levelKey,
        title: copy.levelLabels[levelKey] || levelKey,
        themes: {},
        levelMilestonesByType: {},
      };
    }

    if (LEVEL_MILESTONE_STEP_ORDER.includes(step.step_type)) {
      if (!levels[levelKey].levelMilestonesByType[step.step_type]) {
        levels[levelKey].levelMilestonesByType[step.step_type] = [];
      }
      levels[levelKey].levelMilestonesByType[step.step_type].push(step);
      return;
    }

    if (!levels[levelKey].themes[topicTitle]) {
      levels[levelKey].themes[topicTitle] = {
        key: topicTitle,
        title: topicTitle,
        sortOrder: Number(step.sort_order) || 0,
        chunks: {},
      };
    }

    if (!levels[levelKey].themes[topicTitle].chunks[chunkKey]) {
      levels[levelKey].themes[topicTitle].chunks[chunkKey] = {
        key: chunkKey,
        sortOrder: Number(step.sort_order) || 0,
        stepsByType: {},
        milestoneByType: {},
      };
    }

    const chunk = levels[levelKey].themes[topicTitle].chunks[chunkKey];
    levels[levelKey].themes[topicTitle].sortOrder = Math.min(
      levels[levelKey].themes[topicTitle].sortOrder,
      Number(step.sort_order) || 0,
    );
    chunk.sortOrder = Math.min(chunk.sortOrder, Number(step.sort_order) || 0);

    if (PRIMARY_STEP_ORDER.includes(step.step_type)) {
      chunk.stepsByType[step.step_type] = step;
      return;
    }

    if (!chunk.milestoneByType[step.step_type]) {
      chunk.milestoneByType[step.step_type] = [];
    }
    chunk.milestoneByType[step.step_type].push(step);
  });

  return LEVEL_ORDER.map((levelKey) => {
    const level = levels[levelKey];
    if (!level) {
      return {
        key: levelKey,
        title: copy.levelLabels[levelKey] || levelKey,
        themes: [],
        levelMilestonesByType: {},
        totalSteps: 0,
        completedSteps: 0,
        hasProgress: false,
      };
    }

    const themes = Object.values(level.themes)
      .map((theme) => ({
        ...theme,
        chunks: Object.values(theme.chunks)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((chunk) => ({
            ...chunk,
            focusType: getFocusType(chunk, lastActiveStep?.id || null),
          })),
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const allSteps = themes.flatMap((theme) =>
      theme.chunks.flatMap((chunk) => [
        ...PRIMARY_STEP_ORDER.map((stepType) => chunk.stepsByType[stepType]).filter(Boolean),
        ...getMilestoneSteps(chunk),
      ]),
    );

    return {
      ...level,
      themes,
      levelMilestonesByType: level.levelMilestonesByType,
      totalSteps: [...allSteps, ...getLevelMilestoneSteps(level)].length,
      completedSteps: [...allSteps, ...getLevelMilestoneSteps(level)].filter(
        (step) => getStepCompletion(step).completed,
      ).length,
      hasProgress: [...allSteps, ...getLevelMilestoneSteps(level)].some(
        (step) => Number(step?.progress_count) > 0 || Boolean(step?.completed),
      ),
    };
  });
};

const renderPrimaryStep = ({
  chunkKey,
  index,
  stepType,
  step,
  isFocused,
  isLocked,
  showLabel,
  onOpenStep,
  onLockedAccess,
  stepRefs,
  stepLabels,
  lockedLabel,
}) => {
  if (!step) {
    return (
      <React.Fragment key={`${chunkKey}_${stepType}`}>
        <div className={`${styles.stepCard} ${styles.stepGhost} ${isLocked ? styles.stepCardLocked : ''}`}>
          <div
            className={`${styles.progressShell} ${styles.progressShellSmall} ${isLocked ? styles.progressShellLocked : ''}`}
            style={{ '--progress-angle': '0deg' }}
          >
            <div className={`${styles.stepCore} ${styles.stepCoreGhost} ${isLocked ? styles.stepCoreLocked : ''}`}>
              {isLocked ? <Lock className={styles.stepLockIcon} strokeWidth={2.2} /> : '0/0'}
            </div>
          </div>
          {showLabel && <div className={styles.stepLabelText}>{stepLabels[stepType]}</div>}
        </div>
        {index < PRIMARY_STEP_ORDER.length - 1 && (
          <div className={styles.stepArrow} aria-hidden="true">
            →
          </div>
        )}
      </React.Fragment>
    );
  }

  const { progress, total, percent, completed } = getStepCompletion(step);
  const TopicIcon = getStepTopicIcon(step);
  const accent = getStepTopicAccent(step);
  const isLearn = stepType === 'learn';
  const progressShellTypeClass =
    stepType === 'easy' ? styles.progressShellEasy : stepType === 'medium' ? styles.progressShellMedium : '';
  const handleStepClick = () => {
    if (isLocked) {
      onLockedAccess?.();
      return;
    }
    onOpenStep(step);
  };

  return (
    <React.Fragment key={step.id}>
      <button
        className={`${styles.stepCard} ${isFocused ? styles.stepCardFocused : ''} ${isLocked ? styles.stepCardLocked : ''}`}
        onClick={handleStepClick}
        ref={(node) => {
          if (node) stepRefs.current[step.id] = node;
        }}
      >
        {isLearn ? (
          <div
            className={`${styles.stepCore} ${styles.learnCore} ${styles.learnCoreStandalone} ${
              completed ? styles.learnCoreComplete : ''
            } ${isLocked ? styles.stepCoreLocked : ''}`}
          >
            {isLocked ? (
              <Lock className={styles.stepLockIcon} strokeWidth={2.2} />
            ) : (
              <>
                <div
                  className={`${styles.learnFill} ${completed ? styles.learnFillComplete : ''}`}
                  style={{ height: `${percent}%` }}
                />
                <span className={styles.learnValue}>{progress}/{total}</span>
              </>
            )}
          </div>
        ) : (
          <div
            className={`${styles.progressShell} ${
              isFocused ? styles.progressShellFocused : styles.progressShellSmall
            } ${progressShellTypeClass} ${completed ? styles.progressShellComplete : ''} ${
              isLocked ? styles.progressShellLocked : ''
            }`}
            style={{
              '--progress-angle': `${(isLocked ? 0 : percent) * 3.6}deg`,
              '--topic-accent': accent.color,
              '--topic-glow': accent.glow,
            }}
          >
            <div className={`${styles.stepCore} ${styles.iconCore} ${completed ? styles.stepCoreComplete : ''} ${isLocked ? styles.stepCoreLocked : ''}`}>
              {isLocked ? (
                <Lock className={styles.stepLockIcon} strokeWidth={2.2} />
              ) : (
                <TopicIcon className={styles.topicIcon} strokeWidth={2.1} />
              )}
            </div>
          </div>
        )}
        {showLabel && <div className={styles.stepLabelText}>{stepLabels[stepType]}</div>}
        {isLocked && <span className={styles.stepLockLabel}>{lockedLabel}</span>}
      </button>
      {index < PRIMARY_STEP_ORDER.length - 1 && (
        <div className={styles.stepArrow} aria-hidden="true">
          →
        </div>
      )}
    </React.Fragment>
  );
};

const renderActiveChunkStepRow = ({
  chunk,
  isLocked,
  showPrimaryLabels,
  onOpenStep,
  onLockedAccess,
  stepRefs,
  copy,
}) => (
  <div className={styles.activeChunkShell}>
    <div className={styles.practiceOverlay} aria-hidden="true">
      <div className={styles.practiceBrace} aria-hidden="true">
        <span className={styles.practiceBraceGlyph}>{'{'}</span>
        <span className={styles.practiceBraceGlyph}>{'}'}</span>
      </div>
      <div className={styles.practiceHeading}>{copy.activeRecall}</div>
      <div className={styles.practiceSubheading}>{copy.speakingPractice}</div>
    </div>
    <div className={`${styles.stepRow} ${styles.stepRowActive}`}>
      {PRIMARY_STEP_ORDER.map((stepType, index) =>
        renderPrimaryStep({
          chunkKey: chunk.key,
          index,
          stepType,
          step: chunk.stepsByType[stepType],
          isFocused: chunk.focusType === stepType,
          isLocked,
          showLabel: showPrimaryLabels,
          onOpenStep,
          onLockedAccess,
          stepRefs,
          stepLabels: copy.stepTypeLabels,
          lockedLabel: copy.lockedLabel,
        }))}
    </div>
  </div>
);

const renderMilestoneOrbit = ({
  steps,
  onOpenStep,
  stepRefs,
  copy,
  putHintCounts,
  putHintBusyByType,
  onPutHintClick,
  isLocked,
  onLockedAccess,
  lockedLabel,
}) => {
  let dialogStepIndex = 0;
  let examStepIndex = 0;

  return (
    <div className={styles.milestoneOrbit}>
      {MILESTONE_ARROWS.map((arrow, index) => (
        <span
          key={`arrow_${index + 1}`}
          className={styles.milestoneArrow}
          style={{ left: arrow.left, top: arrow.top, '--arrow-rotate': arrow.rotate }}
          aria-hidden="true"
        >
          ➜
        </span>
      ))}

      {steps.map((step, index) => {
        const position = MILESTONE_LAYOUT[index] || MILESTONE_LAYOUT[MILESTONE_LAYOUT.length - 1];
        const { progress, total, completed, percent } = getStepCompletion(step);
        const imgSrc = getStepTopicImage(step);
        const MilestoneIcon = getStepTopicIcon(step);
        const shortLabel = copy.milestoneShortLabels[step.step_type] || '•';
        const title = copy.milestoneLabels[step.step_type] || copy.fallbackTopic;

        let putHintTrigger = null;
        if (step.step_type === 'dialog') {
          const currentIndex = dialogStepIndex;
          dialogStepIndex += 1;
          if (currentIndex < (Number(putHintCounts?.dialog) || 0)) {
            putHintTrigger = 'dialog';
          }
        } else if (step.step_type === 'exam') {
          const currentIndex = examStepIndex;
          examStepIndex += 1;
          if (currentIndex < (Number(putHintCounts?.vopros) || 0)) {
            putHintTrigger = 'vopros';
          }
        }

        return (
          <button
            className={`${styles.octaNode} ${isLocked ? styles.octaNodeLocked : ''}`}
            key={step.id}
            onClick={() => (isLocked ? onLockedAccess?.() : onOpenStep(step))}
            title={title}
            style={{ left: position.left, top: position.top }}
            ref={(node) => {
              if (node) stepRefs.current[step.id] = node;
            }}
          >
            <div
              className={`${styles.octaShell} ${completed ? styles.octaShellDone : ''} ${isLocked ? styles.octaShellLocked : ''}`}
              style={{ '--progress-angle': `${(isLocked ? 0 : percent) * 3.6}deg` }}
            >
              <div
                className={`${styles.octaCore} ${
                  isReviewStepType(step.step_type) ? styles.octaReviewCore : styles.octaTopicCore
                } ${step.step_type === 'exam' ? styles.octaExamCore : ''} ${
                  !completed && imgSrc ? styles.octaCoreDimmed : ''
                } ${isLocked ? styles.octaCoreLocked : ''}`}
              >
                {isLocked ? (
                  <Lock className={styles.octaLockIcon} strokeWidth={2.2} />
                ) : (
                  <>
                    {imgSrc && <div className={styles.octaImage} style={{ backgroundImage: `url(${imgSrc})` }} />}
                    {isReviewStepType(step.step_type) ? (
                      <span className={styles.octaReviewText}>{`${progress}/${total}`}</span>
                    ) : !imgSrc ? (
                      <MilestoneIcon className={styles.octaIcon} strokeWidth={2.1} />
                    ) : null}
                  </>
                )}
                <span className={styles.octaBadge}>{isLocked ? lockedLabel : shortLabel}</span>
              </div>
            </div>

            {!isLocked && putHintTrigger && (
              <span
                className={`${styles.octaHintMark} ${putHintBusyByType?.[putHintTrigger] ? styles.octaHintBusy : ''}`}
                role="button"
                tabIndex={0}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onPutHintClick(putHintTrigger);
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  event.stopPropagation();
                  onPutHintClick(putHintTrigger);
                }}
                title="?"
              >
                ?
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

const renderLevelMilestoneBlock = ({ level, onOpenStep, stepRefs, copy }) => {
  const steps = LEVEL_MILESTONE_STEP_ORDER
    .map((stepType) => level.levelMilestonesByType?.[stepType]?.[0] || null)
    .filter(Boolean);

  if (steps.length === 0) return null;

  return (
    <div className={styles.levelMilestoneSection}>
      <div className={styles.levelMilestoneIntro}>
        <div className={styles.levelMilestoneEyebrow}>{copy.levelMilestoneEyebrow}</div>
        <h3 className={styles.levelMilestoneTitle}>{copy.levelMilestoneTitle}</h3>
        <p className={styles.levelMilestoneText}>{copy.levelMilestoneText}</p>
      </div>

      <div className={styles.levelMilestoneGrid}>
        {steps.map((step) => {
          const { progress, total, percent, completed } = getStepCompletion(step);
          const isHard = step.step_type === 'level_review_medium';

          return (
            <button
              className={`${styles.levelMilestoneCard} ${
                isHard ? styles.levelMilestoneCardHard : styles.levelMilestoneCardEasy
              } ${completed ? styles.levelMilestoneCardDone : ''}`}
              key={step.id}
              onClick={() => onOpenStep(step)}
              ref={(node) => {
                if (node) stepRefs.current[step.id] = node;
              }}
            >
              <div className={styles.levelMilestoneTop}>
                <span className={styles.levelMilestoneBadge}>
                  {copy.levelMilestoneLabels[step.step_type] || copy.activeRecall}
                </span>
                <span className={styles.levelMilestoneProgress}>{progress}/{total}</span>
              </div>

              <div className={styles.levelMilestoneCardTitle}>
                {copy.levelMilestoneDescriptions[step.step_type] || copy.levelMilestoneMeta}
              </div>

              <div className={styles.levelMilestoneTrack} aria-hidden="true">
                <span
                  className={styles.levelMilestoneTrackFill}
                  style={{ width: `${percent}%` }}
                />
              </div>

              <div className={styles.levelMilestoneFooter}>
                <span>{copy.levelMilestoneMeta}</span>
                <span>{percent}%</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const PathHome = ({
  userId,
  language,
  targetLanguage,
  vipLevel = 0,
  phrasesCount,
  legacyMode = false,
  canToggleLegacyMode = false,
  onSelectLegacy,
  onLegacyModeChange,
  onLevelChange,
  selectedLevel,
  onGoToVip,
  onOpenLessons,
  onOpenStep,
  showPwaInfoTrigger = false,
  onOpenPwaInfo,
}) => {
  const { t } = useAppConfig();
  const { actions } = useAssistant();
  const copy = useMemo(() => getPathCopy(t), [t]);
  const isFreeTier = Number(vipLevel) <= 0;
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLevel, setExpandedLevel] = useState(undefined);
  const [lessonOverview, setLessonOverview] = useState({ themes: 0, subthemes: 0, words: 0 });
  const [lessonCollapsed, setLessonCollapsed] = useState(false);
  const [putHintCounts, setPutHintCounts] = useState(DEFAULT_PUT_HINT_COUNTS);
  const [putHintBusyByType, setPutHintBusyByType] = useState({
    vopros: false,
    dialog: false,
    nullteil: false,
  });
  const stepRefs = useRef({});
  const canShowModeSwitch = Boolean(canToggleLegacyMode);
  const canShowPwaHelp = Boolean(showPwaInfoTrigger);
  const canShowTopActionsRow = canShowModeSwitch || canShowPwaHelp;
  const showLegacy = canShowModeSwitch && Boolean(legacyMode);
  const handleLockedStepOpen = useCallback(() => {
    onGoToVip?.();
  }, [onGoToVip]);

  const loadPutHintCounts = useCallback(async () => {
    if (!userId || !language) return;

    try {
      const response = await fetchData('/api/tips/functional', 'POST', {
        userId,
        language,
        targetLanguage,
        screen: 'home',
        mode: 'put_counts',
      });

      setPutHintCounts({
        ...DEFAULT_PUT_HINT_COUNTS,
        ...(response?.counts || {}),
      });
    } catch (error) {
      console.error('Failed to load put hint counters', error);
    }
  }, [userId, language, targetLanguage]);

  const handlePutHintClick = useCallback(async (triggerKey) => {
    if (!triggerKey || putHintBusyByType?.[triggerKey]) return;

    setPutHintBusyByType((prev) => ({ ...prev, [triggerKey]: true }));

    try {
      const response = await fetchData('/api/tips/functional', 'POST', {
        userId,
        language,
        targetLanguage,
        screen: 'home',
        mode: 'put_tip',
        trigger: triggerKey,
      });

      const tip = Array.isArray(response?.tips) ? response.tips[0] : null;
      if (tip?.text) {
        const audioSrc = tip?.audioSrc || tip?.audio || tip?.audioUrl || null;
        actions.resetState();
        await actions.speakText(tip.text, { variant: 'sticky', audioSrc });
      }

      await loadPutHintCounts();
    } catch (error) {
      console.error('Failed to play put hint', error);
    } finally {
      setPutHintBusyByType((prev) => ({ ...prev, [triggerKey]: false }));
    }
  }, [actions, language, loadPutHintCounts, putHintBusyByType, targetLanguage, userId]);

  useEffect(() => {
    let active = true;

    const loadHomeData = async () => {
      try {
        setLoading(true);
        const [stepsResult, lessonsResult] = await Promise.allSettled([
          fetchPathSteps({ userId }),
          fetchLessonTopics({ userId }),
        ]);

        if (!active) return;

        const nextSteps = stepsResult.status === 'fulfilled' ? (stepsResult.value?.steps || []) : [];
        const nextLessons = lessonsResult.status === 'fulfilled' && Array.isArray(lessonsResult.value)
          ? lessonsResult.value
          : [];

        startTransition(() => {
          setSteps(nextSteps);
          setLessonOverview(getLessonOverview(nextLessons));
        });
      } finally {
        if (active) setLoading(false);
      }
    };

    loadHomeData();
    return () => {
      active = false;
    };
  }, [userId]);

  useEffect(() => {
    loadPutHintCounts();
  }, [loadPutHintCounts]);

  const lastActiveStep = useMemo(() => getLastActiveStep(steps), [steps]);
  const levels = useMemo(() => buildLevelGroups(steps, lastActiveStep, copy), [steps, lastActiveStep, copy]);
  const resolvedExpandedLevel = expandedLevel === undefined
    ? (lastActiveStep?.level_group || null)
    : expandedLevel;

  // Auto-collapse lessons if there's progress in main levels
  useEffect(() => {
    if (!levels || loading) return;
    const hasProgressInMainLevels = levels.some((level) => level.hasProgress && LEVEL_ORDER.includes(level.key));
    if (hasProgressInMainLevels) {
      setLessonCollapsed(true);
    }
  }, [levels, loading]);

  useEffect(() => {
    if (!lastActiveStep || resolvedExpandedLevel !== lastActiveStep.level_group) return;
    const node = stepRefs.current[lastActiveStep.id];
    if (!node) return;

    const timeoutId = window.setTimeout(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 140);

    return () => window.clearTimeout(timeoutId);
  }, [resolvedExpandedLevel, lastActiveStep]);

  if (showLegacy) {
    return (
      <div className={styles.legacyWrapper}>
        {canShowModeSwitch && (
          <div className={styles.modeSwitchRow}>
            <button className={styles.modeSwitchButton} onClick={() => onLegacyModeChange?.(false)}>
              {copy.switchPath}
            </button>
          </div>
        )}
        <MainMenu
          onSelect={onSelectLegacy}
          phrasesCount={phrasesCount}
          onLevelChange={onLevelChange}
          selectedLevel={selectedLevel}
          onGoToVip={onGoToVip}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {canShowTopActionsRow && (
        <div className={styles.modeSwitchRow}>
          {canShowModeSwitch && (
            <button className={styles.modeSwitchButton} onClick={() => onLegacyModeChange?.(true)}>
              {copy.switchModule}
            </button>
          )}
          {canShowPwaHelp && (
            <button
              className={styles.pwaHelpButton}
              onClick={() => onOpenPwaInfo?.()}
              aria-label="PWA info"
              title="PWA info"
            >
              ?
            </button>
          )}
        </div>
      )}
      <div className={styles.levelStack}>
        <section className={`${styles.lessonsIntroCard} ${lessonCollapsed ? styles.lessonsIntroCardCompact : ''}`}>
          <button
            className={styles.lessonsIntroHeader}
            onClick={() => setLessonCollapsed(!lessonCollapsed)}
          >
            <div className={styles.lessonsIntroIconWrap} aria-hidden="true">
              <BookOpenText className={styles.lessonsIntroIcon} strokeWidth={2.1} />
            </div>
            <div className={styles.lessonsIntroCopy}>
              <div className={styles.lessonsIntroBadge}>{copy.lessonsBadge}</div>
              <h2 className={styles.lessonsIntroTitle}>{copy.lessonsTitle}</h2>
              {!lessonCollapsed && (
                <>
                  <p className={styles.lessonsIntroText}>{copy.lessonsSubtitle}</p>
                  <div className={styles.lessonsIntroMeta}>
                    <span className={styles.lessonsIntroPill}>{copy.lessonsThemes}: {lessonOverview.themes}</span>
                    <span className={styles.lessonsIntroPill}>{copy.lessonsSubthemes}: {lessonOverview.subthemes}</span>
                    <span className={styles.lessonsIntroPill}>{copy.lessonsWords}: {lessonOverview.words}</span>
                  </div>
                  {(Number(putHintCounts.nullteil) || 0) > 0 && (
                    <div className={styles.lessonsHintRow}>
                      {Array.from({ length: Number(putHintCounts.nullteil) || 0 }).map((_, index) => (
                        <span
                          key={`nullteil_hint_${index + 1}`}
                          className={`${styles.lessonsHintMark} ${putHintBusyByType.nullteil ? styles.lessonsHintBusy : ''}`}
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handlePutHintClick('nullteil');
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter' && event.key !== ' ') return;
                            event.preventDefault();
                            event.stopPropagation();
                            handlePutHintClick('nullteil');
                          }}
                          title="?"
                        >
                          ?
                        </span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            {lessonCollapsed && <div className={styles.lessonsIntroChevron}>+</div>}
            {!lessonCollapsed && <button className={styles.lessonsIntroButton} onClick={(e) => { e.stopPropagation(); onOpenLessons(); }}>
              {copy.lessonsOpen}
            </button>}
          </button>
        </section>

        {loading ? (
          <div className={styles.loading}>{copy.loading}</div>
        ) : (
          <>
            {steps.length === 0 && <div className={styles.loading}>{copy.empty}</div>}

            {levels.map((level) => {
            const isExpanded = resolvedExpandedLevel === level.key;
            const isLastActiveLevel = lastActiveStep?.level_group === level.key;
            const totalChunksInLevel = level.themes.reduce(
              (sum, theme) => sum + (Array.isArray(theme?.chunks) ? theme.chunks.length : 0),
              0,
            );
            const freeUnlockedChunkCount = Math.ceil(totalChunksInLevel / 2);

            return (
              <section
                className={`${styles.levelCard} ${isExpanded ? styles.levelExpanded : ''}`}
                key={level.key}
              >
                <button
                  className={styles.levelToggle}
                  onClick={() => setExpandedLevel((prev) => {
                    const current = prev === undefined ? (lastActiveStep?.level_group || null) : prev;
                    return current === level.key ? null : level.key;
                  })}
                >
                  <div className={styles.levelMeta}>
                    <div className={styles.levelTitleRow}>
                      <h2 className={styles.levelTitle}>{level.title}</h2>
                      {isLastActiveLevel && <span className={styles.levelBadge}>{copy.activeLevel}</span>}
                    </div>
                    <div className={styles.levelStats}>
                      <span>{level.completedSteps}/{level.totalSteps || 0}</span>
                      <span>{level.hasProgress ? copy.hasProgress : copy.notStarted}</span>
                    </div>
                  </div>
                  <div className={`${styles.levelChevron} ${isExpanded ? styles.levelChevronOpen : ''}`}>
                    {isExpanded ? '−' : '+'}
                  </div>
                </button>

                {isExpanded && (
                  <div className={styles.levelBody}>
                    {level.themes.map((theme, themeIndex) => {
                      const chunkLevelStartIndex = level.themes
                        .slice(0, themeIndex)
                        .reduce((sum, currentTheme) => sum + (currentTheme?.chunks?.length || 0), 0);

                      return (
                      <div className={styles.themeSection} key={`${level.key}_${theme.key}`}>
                        <div className={styles.themeBand}>
                          <div className={styles.themeHeader}>
                            <div className={styles.themePulse} />
                            <div className={styles.themeTitle}>{theme.title}</div>
                          </div>

                          <div className={styles.chunkList}>
                            {theme.chunks.map((chunk, chunkIndex) => {
                            const chunkLevelIndex = chunkLevelStartIndex + chunkIndex;
                            const isChunkLocked = isFreeTier && chunkLevelIndex >= freeUnlockedChunkCount;
                            const isFirstLevelChunk = chunkLevelIndex === 0;
                            const isActiveChunk = PRIMARY_STEP_ORDER.some(
                              (stepType) => chunk.stepsByType[stepType]?.id === lastActiveStep?.id,
                            );
                            // Show labels on active chunk, or once on the first chunk of the entire level when there is no progress.
                            const showPrimaryLabels =
                              !isChunkLocked && (isActiveChunk || (isFirstLevelChunk && isExpanded && !level.hasProgress));

                              return (
                                <div
                                  className={`${styles.chunkRow} ${
                                    isChunkLocked ? styles.chunkRowLocked : ''
                                  } ${
                                    isChunkFullyCompleted(chunk) ? styles.chunkRowComplete : ''
                                  }`}
                                  key={`${theme.key}_${chunk.key}`}
                                >
                                  {showPrimaryLabels
                                    ? renderActiveChunkStepRow({
                                      chunk,
                                      isLocked: isChunkLocked,
                                      showPrimaryLabels,
                                      onOpenStep,
                                      onLockedAccess: handleLockedStepOpen,
                                      stepRefs,
                                      copy,
                                    })
                                    : (
                                      <div className={styles.stepRow}>
                                        {PRIMARY_STEP_ORDER.map((stepType, index) =>
                                          renderPrimaryStep({
                                            chunkKey: chunk.key,
                                            index,
                                            stepType,
                                            step: chunk.stepsByType[stepType],
                                            isFocused: chunk.focusType === stepType,
                                            isLocked: isChunkLocked,
                                            showLabel: showPrimaryLabels,
                                            onOpenStep,
                                            onLockedAccess: handleLockedStepOpen,
                                            stepRefs,
                                            stepLabels: copy.stepTypeLabels,
                                            lockedLabel: copy.lockedLabel,
                                          }))}
                                      </div>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {theme.chunks.map((chunk, chunkIndex) => {
                          const dialogSteps = chunk.milestoneByType.dialog || [];
                          const examSteps = chunk.milestoneByType.exam || [];
                          const reviewEasyStep = chunk.milestoneByType.review_easy?.[0] || null;
                          const reviewMediumStep = chunk.milestoneByType.review_medium?.[0] || null;
                          const chunkLevelIndex = chunkLevelStartIndex + chunkIndex;
                          const isChunkLocked = isFreeTier && chunkLevelIndex >= freeUnlockedChunkCount;
                          const milestoneSteps = [
                            ...dialogSteps,
                            ...examSteps,
                            reviewEasyStep,
                            reviewMediumStep,
                          ].filter(Boolean);

                          if (!hasMilestoneBlock(chunk)) return null;

                          return (
                            <div className={styles.milestoneBridge} key={`${theme.key}_${chunk.key}_milestone`}>
                              {renderMilestoneOrbit({
                                steps: milestoneSteps,
                                onOpenStep,
                                stepRefs,
                                copy,
                                putHintCounts,
                                putHintBusyByType,
                                onPutHintClick: handlePutHintClick,
                                isLocked: isChunkLocked,
                                onLockedAccess: handleLockedStepOpen,
                                lockedLabel: copy.lockedLabel,
                              })}
                            </div>
                          );
                        })}
                      </div>
                    )})}

                    {hasLevelMilestoneBlock(level) &&
                      renderLevelMilestoneBlock({
                        level,
                        onOpenStep,
                        stepRefs,
                        copy,
                      })}
                  </div>
                )}
              </section>
            );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default PathHome;
